import { compactUsd, usd, type Overview } from "./reportsApi";

/**
 * Reports landing charts. Same idiom as the console Dashboard (CSS-grid bars, `ffa-trend` / `ffa-hbars`) and the
 * portal's viz (lime = the measure, cyan = the secondary series), colours only from the --ff-viz-* tokens. Every
 * chart has a text table for screen readers; nothing here is decorative or estimated.
 */
const monthFmt = new Intl.DateTimeFormat("en-US", { month: "short", timeZone: "UTC" });
const monthLong = new Intl.DateTimeFormat("en-US", { month: "long", year: "numeric", timeZone: "UTC" });
const asMonth = (m: string) => new Date(`${m}-15T12:00:00Z`);

export function RevenueTrendChart({ data }: { data: Overview["revenueTrend"] & { showsCost: boolean } }) {
  const months = data.months;
  const max = Math.max(1, ...months.map((m) => m.revenueCents));
  const total = months.reduce((n, m) => n + m.revenueCents, 0);
  const margin = months.reduce((n, m) => n + (m.marginCents ?? 0), 0);
  const label = `Revenue by month, ${monthLong.format(asMonth(months[0]?.month ?? data.from.slice(0, 7)))} to ${monthLong.format(asMonth(months.at(-1)?.month ?? data.to.slice(0, 7)))}: ${usd.format(total / 100)}${data.showsCost ? `, margin ${usd.format(margin / 100)}` : ""}.`;
  return (
    <figure className="ffa-trend ffa-rtrend">
      <div className="ffa-trend__plot" role="img" aria-label={label}>
        <div className="ffa-trend__scale" aria-hidden="true">
          <span>{compactUsd.format(max / 100)}</span>
          <span>{compactUsd.format(max / 200)}</span>
          <span>$0</span>
        </div>
        <div className="ffa-trend__bars" style={{ gridTemplateColumns: `repeat(${months.length}, minmax(0, 1fr))` }} aria-hidden="true">
          {months.map((m) => (
            <div key={m.month} className="ffa-trend__col" title={`${monthLong.format(asMonth(m.month))}: ${usd.format(m.revenueCents / 100)}${m.marginCents !== undefined ? `, margin ${usd.format(m.marginCents / 100)}` : ""}, ${m.shipments} shipment${m.shipments === 1 ? "" : "s"}`}>
              <div className="ffa-trend__bar" style={{ blockSize: `${(m.revenueCents / max) * 100}%` }}>
                {m.marginCents !== undefined && m.revenueCents > 0 ? <div className="ffa-trend__margin" style={{ blockSize: `${Math.max(0, (m.marginCents / m.revenueCents) * 100)}%` }} /> : null}
              </div>
              <span className="ffa-trend__x ffa-rtrend__x">{monthFmt.format(asMonth(m.month))}</span>
            </div>
          ))}
        </div>
      </div>
      <figcaption className="ffa-chartlegend">
        <span><i className="ffa-swatch ffa-swatch--lime" aria-hidden="true" />Revenue {usd.format(total / 100)}</span>
        {data.showsCost ? <span><i className="ffa-swatch ffa-swatch--band" aria-hidden="true" />Margin {usd.format(margin / 100)}{total ? ` (${Math.round((margin / total) * 1000) / 10}%)` : ""}</span> : <span className="ffa-muted">Margin is hidden for your role.</span>}
      </figcaption>
      <table className="ffx-sr">
        <caption>Revenue{data.showsCost ? " and margin" : ""} by month</caption>
        <thead>
          <tr><th scope="col">Month</th><th scope="col">Shipments</th><th scope="col">Revenue</th>{data.showsCost ? <th scope="col">Margin</th> : null}</tr>
        </thead>
        <tbody>
          {months.map((m) => (
            <tr key={m.month}><th scope="row">{monthLong.format(asMonth(m.month))}</th><td>{m.shipments}</td><td>{usd.format(m.revenueCents / 100)}</td>{data.showsCost ? <td>{usd.format((m.marginCents ?? 0) / 100)}</td> : null}</tr>
          ))}
        </tbody>
      </table>
    </figure>
  );
}

export function FunnelChart({ steps }: { steps: Overview["funnel"]["steps"] }) {
  const top = Math.max(1, ...steps.map((s) => s.value ?? 0));
  return (
    <ol className="ffa-funnel" aria-label="Quote conversion funnel, last 90 days">
      {steps.map((s, i) => (
        <li key={s.key} className={s.value === null ? "ffa-funnel__step is-nodata" : "ffa-funnel__step"}>
          <div className="ffa-funnel__head">
            <span className="ffa-funnel__label">{s.label}</span>
            {s.value === null ? (
              <span className="ffa-nodata">No data source</span>
            ) : (
              <span className="ffx-num ffa-cell-strong">
                {s.value.toLocaleString("en-US")}
                {s.pctOfPrev !== undefined && s.pctOfPrev !== null ? <span className="ffa-funnel__pct"> {s.pctOfPrev}% of {steps[i - 1]?.label.toLowerCase()}</span> : null}
              </span>
            )}
          </div>
          {s.value === null ? (
            <p className="ffa-small ffa-muted ffa-funnel__note">{s.note?.replace(/^No data source: /, "")}</p>
          ) : (
            <span className="ffa-funnel__track" aria-hidden="true">
              <span className="ffa-funnel__bar" style={{ inlineSize: `${(s.value / top) * 100}%` }} />
            </span>
          )}
        </li>
      ))}
    </ol>
  );
}

export function OnTimeChart({ carriers }: { carriers: Overview["carrierOnTime"] }) {
  return (
    <figure className="ffa-hbars ffa-ontime">
      <ul aria-label="On-time delivery by carrier, last 90 days">
        {carriers.map((c) => (
          <li key={c.carrier}>
            <span className="ffa-hbars__label ffa-trunc" title={c.name}>{c.name}</span>
            {c.onTimePct === null ? (
              <span className="ffa-hbars__track ffa-hbars__track--empty" aria-hidden="true" />
            ) : (
              <span className="ffa-hbars__track" aria-hidden="true"><span className="ffa-hbars__bar ffa-ontime__bar" style={{ inlineSize: `${c.onTimePct}%` }} /></span>
            )}
            <span className="ffx-num ffa-hbars__value">
              {c.onTimePct === null ? <span className="ffa-nodata" title="Delivered shipments without a carrier estimated delivery date can't be measured.">Not measured</span> : `${c.onTimePct}%`}
              <span className="ffa-muted ffa-small"> {c.onTime}/{c.measured}</span>
            </span>
          </li>
        ))}
      </ul>
      <figcaption className="ffa-small ffa-muted">On time = delivered by the carrier&apos;s estimated date (Eastern). Shown as on time / measured deliveries.</figcaption>
    </figure>
  );
}

function Meter({ label, pct, late, lateLabel }: { label: string; pct: number | null; late: number; lateLabel: string }) {
  return (
    <div className="ffa-meter">
      <div className="ffa-meter__head">
        <span>{label}</span>
        <span className="ffx-num ffa-meter__value">{pct === null ? <span className="ffa-nodata">No tickets with this SLA yet</span> : `${pct}%`}</span>
      </div>
      <span className="ffa-meter__track" role="meter" aria-label={label} aria-valuemin={0} aria-valuemax={100} aria-valuenow={pct ?? 0} aria-valuetext={pct === null ? "Not measured" : `${pct}% within SLA, ${late} ${lateLabel}`}>
        <span className={pct !== null && pct < 80 ? "ffa-meter__fill is-low" : "ffa-meter__fill"} style={{ inlineSize: `${pct ?? 0}%` }} />
      </span>
      <span className="ffa-small ffa-muted">{late} {lateLabel}</span>
    </div>
  );
}

export function SlaChart({ sla }: { sla: Overview["ticketSla"] }) {
  return (
    <div className="ffa-sla">
      <Meter label="First response within SLA" pct={sla.overall.firstResponsePct} late={sla.overall.firstResponseLate} lateLabel="late" />
      <Meter label="Resolved within SLA" pct={sla.overall.resolutionPct} late={sla.overall.resolutionLate} lateLabel="past due" />
      {sla.queues.length ? (
        <table className="ffx-table ffa-minitable">
          <caption className="ffx-sr">SLA by queue, last 90 days</caption>
          <thead>
            <tr><th scope="col">Queue</th><th scope="col" className="ffx-table__num">Opened</th><th scope="col" className="ffx-table__num">First response</th><th scope="col" className="ffx-table__num">Resolution</th></tr>
          </thead>
          <tbody>
            {sla.queues.map((q) => (
              <tr key={q.queue}>
                <th scope="row">{q.queue[0]!.toUpperCase() + q.queue.slice(1)}</th>
                <td className="ffx-table__num ffx-num">{q.opened}</td>
                <td className="ffx-table__num ffx-num">{q.firstResponse.pct === null ? "-" : `${q.firstResponse.pct}%`}</td>
                <td className="ffx-table__num ffx-num">{q.resolution.pct === null ? "-" : `${q.resolution.pct}%`}</td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <p className="ffa-small ffa-muted">No tickets opened in the last 90 days.</p>
      )}
    </div>
  );
}
